import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthUser, LoginRequest, LoginResponse } from "@bi/types";
import { api, tokenStore } from "./api";
import { useQueryClient } from "@tanstack/react-query";

interface AuthState {
  user: AuthUser | null;
  ready: boolean;
  signIn: (creds: LoginRequest) => Promise<void>;
  signOut: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  // tenta restaurar sessão a partir do token salvo
  useEffect(() => {
    const t = tokenStore.get();
    if (!t) { setReady(true); return; }
    api.get<AuthUser>("/me")
      .then((u) => { if (tokenStore.get() === t) setUser(u); })
      .catch(() => { if (tokenStore.get() === t) tokenStore.clear(); })
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    const encerrar = () => { tokenStore.clear(); queryClient.clear(); setUser(null); };
    const atualizar = () => {
      const token = tokenStore.get();
      if (!token || document.visibilityState !== "visible") return;
      api.get<AuthUser>("/me").then(next => {
        if (tokenStore.get() !== token) return;
        if (user && (user.role !== next.role || user.companyId !== next.companyId)) queryClient.clear();
        setUser(next);
      }).catch((error) => {
        if (error?.status === 401 && tokenStore.get() === token) { tokenStore.clear(); queryClient.clear(); setUser(null); }
      });
    };
    window.addEventListener("focus", atualizar);
    window.addEventListener("siag:refresh-session", atualizar);
    window.addEventListener("siag:session-ended", encerrar);
    document.addEventListener("visibilitychange", atualizar);
    return () => {
      window.removeEventListener("focus", atualizar);
      window.removeEventListener("siag:refresh-session", atualizar);
      window.removeEventListener("siag:session-ended", encerrar);
      document.removeEventListener("visibilitychange", atualizar);
    };
  }, [queryClient, user?.role, user?.companyId]);

  // Empresa de demonstração usa a identidade azul (variáveis em globals.css).
  useEffect(() => {
    if (user?.isDemo) document.documentElement.dataset.brand = "demo";
    else delete document.documentElement.dataset.brand;
  }, [user?.isDemo]);

  const value = useMemo<AuthState>(() => ({
    user,
    ready,
    async signIn(creds) {
      const res = await api.post<LoginResponse>("/auth/login", creds);
      queryClient.clear();
      tokenStore.set(res.token);
      setUser(res.user);
    },
    signOut() {
      tokenStore.clear();
      queryClient.clear();
      setUser(null);
    },
  }), [user, ready, queryClient]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth fora do AuthProvider");
  return ctx;
}
